import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { zl, useCart } from "@/lib/shop";
import { useShopAuth } from "@/lib/shopAuth";
import ShopLayout from "./ShopLayout";
import InpostGeowidget from "@/components/shop/InpostGeowidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", street: "", postal: "", city: "", note: "" };

export default function ShopCheckout() {
  const { items, subtotal, clear } = useCart();
  const { customer } = useShopAuth();
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [threshold, setThreshold] = useState(0);
  const [method, setMethod] = useState("");
  const [point, setPoint] = useState("");
  const [pointAddr, setPointAddr] = useState("");
  const [geoToken, setGeoToken] = useState("");
  const [f, setF] = useState(EMPTY);
  const [invoice, setInvoice] = useState(false);
  const [inv, setInv] = useState({ company: "", nip: "", address: "" });
  const [cTerms, setCTerms] = useState(false);
  const [cPrivacy, setCPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (items.length === 0) navigate("/sklep/koszyk");
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!customer) return;
    const parts = (customer.name || "").trim().split(/\s+/);
    const sh = customer.shipping || {};
    setF((p) => ({
      ...p,
      first_name: p.first_name || parts[0] || "",
      last_name: p.last_name || parts.slice(1).join(" ") || "",
      email: p.email || customer.email || "",
      phone: p.phone || sh.phone || "",
      street: p.street || sh.street || "",
      postal: p.postal || sh.postal || "",
      city: p.city || sh.city || "",
    }));
    const ci = customer.invoice || {};
    if (ci.company || ci.nip) {
      setInvoice(true);
      setInv((p) => ({ company: p.company || ci.company || "", nip: p.nip || ci.nip || "", address: p.address || ci.address || "" }));
    }
  }, [customer]);

  useEffect(() => {
    api.get("/shop/settings").then((r) => {
      const m = r.data.shipping_methods || [];
      setMethods(m); setThreshold(r.data.free_shipping_threshold_grosz || 0);
      setGeoToken(r.data.inpost_geo_token || "");
      if (m[0]) setMethod(m[0].id);
    });
  }, []);

  const sel = methods.find((m) => m.id === method);
  let shipping = sel ? sel.price_grosz : 0;
  if (threshold && subtotal >= threshold) shipping = 0;
  const total = subtotal + shipping;

  const pay = async () => {
    if (!f.first_name.trim() || !f.last_name.trim() || !f.email.trim()) return toast.error("Uzupełnij imię, nazwisko i e-mail");
    if (sel?.point_required && !point.trim()) return toast.error("Podaj kod paczkomatu InPost");
    if (!sel?.point_required && sel?.type !== "pickup" && (!f.street.trim() || !f.city.trim())) return toast.error("Uzupełnij adres dostawy");
    if (!cTerms || !cPrivacy) return toast.error("Zaakceptuj regulamin i politykę prywatności");
    setBusy(true);
    try {
      const { data } = await api.post("/shop/checkout", {
        items: items.map((it) => ({ product_id: it.product_id, color: it.color, size: it.size, quantity: it.quantity })),
        shipping_method: method, inpost_point: point,
        first_name: f.first_name, last_name: f.last_name, email: f.email, phone: f.phone,
        street: f.street, postal: f.postal, city: f.city,
        want_invoice: invoice, inv_company: inv.company, inv_nip: inv.nip, inv_address: inv.address,
        note: f.note, consent_terms: cTerms, consent_privacy: cPrivacy,
      });
      clear();
      window.location.assign(data.redirectUri);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się rozpocząć płatności");
      setBusy(false);
    }
  };

  const inp = "bg-white/5 border-white/10 text-neutral-100 placeholder:text-neutral-500 mt-1";

  return (
    <ShopLayout>
      <div className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight mb-8">Zamówienie</h1>
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <div className="space-y-8">
            <section>
              <h2 className="text-sm font-bold uppercase tracking-wider text-amber-400 mb-3">Dane kupującego</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Imię *</Label><Input data-testid="co-first" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className={inp} /></div>
                <div><Label className="text-xs">Nazwisko *</Label><Input data-testid="co-last" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className={inp} /></div>
                <div><Label className="text-xs">E-mail *</Label><Input data-testid="co-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inp} /></div>
                <div><Label className="text-xs">Telefon</Label><Input data-testid="co-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></div>
              </div>
            </section>

            <section>
              <h2 className="text-sm font-bold uppercase tracking-wider text-amber-400 mb-3">Dostawa</h2>
              <div className="space-y-2">
                {methods.map((m) => (
                  <label key={m.id} data-testid={`ship-${m.id}`} className={`flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer ${method === m.id ? "border-amber-400 bg-amber-400/10" : "border-white/10 hover:border-white/30"}`}>
                    <span className="flex items-center gap-3">
                      <input type="radio" name="ship" checked={method === m.id} onChange={() => setMethod(m.id)} className="accent-amber-400" />
                      <span className="font-medium">{m.name}</span>
                    </span>
                    <span className="font-bold">{(threshold && subtotal >= threshold) ? "0,00 zł" : zl(m.price_grosz)}</span>
                  </label>
                ))}
              </div>
              {sel?.point_required && (
                <div className="mt-3 rounded-lg border border-white/10 bg-white/5 p-3">
                  <Label className="text-xs flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-amber-400" />Paczkomat InPost *</Label>
                  {geoToken ? (
                    <div className="mt-2">
                      <InpostGeowidget token={geoToken} onSelect={(p) => { setPoint(p.code || ""); setPointAddr(p.address || ""); }} />
                      {point
                        ? <p className="mt-2 text-sm text-amber-300" data-testid="co-inpost-selected">Wybrany paczkomat: <b>{point}</b>{pointAddr ? ` — ${pointAddr}` : ""}</p>
                        : <p className="mt-2 text-[11px] text-neutral-500">Wybierz paczkomat na mapie powyżej.</p>}
                    </div>
                  ) : (
                    <>
                      <div className="mt-1 flex gap-2">
                        <Input data-testid="co-inpost" value={point} onChange={(e) => setPoint(e.target.value.toUpperCase())} placeholder="np. KRA010" className={inp + " mt-0"} />
                        <a href="https://inpost.pl/znajdz-paczkomat" target="_blank" rel="noreferrer"><Button type="button" variant="outline" className="border-white/20 gap-1.5 whitespace-nowrap"><ExternalLink className="h-4 w-4" />Znajdź</Button></a>
                      </div>
                      <p className="mt-1 text-[11px] text-neutral-500">Wyszukaj paczkomat na mapie InPost i wpisz jego kod.</p>
                    </>
                  )}
                </div>
              )}
              {sel && !sel.point_required && sel.type !== "pickup" && (
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2"><Label className="text-xs">Ulica i numer *</Label><Input data-testid="co-street" value={f.street} onChange={(e) => set("street", e.target.value)} className={inp} /></div>
                  <div><Label className="text-xs">Kod pocztowy</Label><Input data-testid="co-postal" value={f.postal} onChange={(e) => set("postal", e.target.value)} className={inp} /></div>
                  <div><Label className="text-xs">Miejscowość *</Label><Input data-testid="co-city" value={f.city} onChange={(e) => set("city", e.target.value)} className={inp} /></div>
                </div>
              )}
            </section>

            <section>
              <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="co-invoice" checked={invoice} onCheckedChange={setInvoice} />Chcę fakturę na firmę</label>
              {invoice && (
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Nazwa firmy</Label><Input value={inv.company} onChange={(e) => setInv({ ...inv, company: e.target.value })} className={inp} /></div>
                  <div><Label className="text-xs">NIP</Label><Input value={inv.nip} onChange={(e) => setInv({ ...inv, nip: e.target.value })} className={inp} /></div>
                  <div className="sm:col-span-2"><Label className="text-xs">Adres firmy</Label><Input value={inv.address} onChange={(e) => setInv({ ...inv, address: e.target.value })} className={inp} /></div>
                </div>
              )}
            </section>

            <section className="space-y-2">
              <label className="flex items-start gap-2 text-sm"><Checkbox data-testid="co-terms" checked={cTerms} onCheckedChange={setCTerms} className="mt-0.5" /><span>Akceptuję <Link to="/sklep/regulamin" target="_blank" className="text-amber-400 underline">Regulamin sklepu</Link> oraz przyjmuję do wiadomości <Link to="/sklep/odstapienie" target="_blank" className="text-amber-400 underline">prawo odstąpienia od umowy</Link>. *</span></label>
              <label className="flex items-start gap-2 text-sm"><Checkbox data-testid="co-privacy" checked={cPrivacy} onCheckedChange={setCPrivacy} className="mt-0.5" /><span>Zapoznałem/am się z <Link to="/sklep/polityka-prywatnosci" target="_blank" className="text-amber-400 underline">Polityką prywatności (RODO)</Link>. *</span></label>
            </section>
          </div>

          <aside className="h-fit rounded-2xl border border-white/10 bg-white/5 p-5 lg:sticky lg:top-20">
            <h2 className="text-sm font-bold uppercase tracking-wider text-amber-400 mb-4">Podsumowanie</h2>
            <div className="space-y-2 text-sm max-h-48 overflow-auto mb-4">
              {items.map((it, i) => (
                <div key={i} className="flex justify-between gap-2"><span className="text-neutral-300 truncate">{it.quantity}× {it.name}</span><span className="font-medium whitespace-nowrap">{zl(it.price_grosz * it.quantity)}</span></div>
              ))}
            </div>
            <div className="border-t border-white/10 pt-3 space-y-1.5 text-sm">
              <div className="flex justify-between text-neutral-400"><span>Produkty</span><span>{zl(subtotal)}</span></div>
              <div className="flex justify-between text-neutral-400"><span>Dostawa</span><span>{zl(shipping)}</span></div>
              <div className="flex justify-between text-lg font-extrabold pt-2"><span>Razem</span><span data-testid="co-total">{zl(total)}</span></div>
            </div>
            <Button data-testid="co-pay-btn" onClick={pay} disabled={busy || items.length === 0} className="mt-5 w-full h-12 bg-amber-400 text-[#0D1117] hover:bg-amber-300 font-bold uppercase tracking-wider gap-2">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" />Przekierowywanie…</> : "Zapłać przez PayU"}
            </Button>
            <p className="mt-3 text-[11px] text-neutral-500 text-center">Płatność BLIK / karta / przelew online obsługiwana przez PayU.</p>
          </aside>
        </div>
      </div>
    </ShopLayout>
  );
}
